/**
 * iter87.465.20 — MLS Round of 16 Series pick modal ("Series
 * Predictor" — renamed to "Sweet 16 Series" in iter87.465.23, reverted
 * back in iter87.465.24 now that the "Round of 16 Series" sub-header
 * carries that branding instead — 16 teams enter Round One, 8 per
 * conference, best-of-3), sibling of `NbaSeriesPickModal.jsx` —
 * simplified to ONE round (Round One is the only best-of-3 round;
 * Wild Card is single-game, Conf Semis+ use the reused Match
 * Predictor/Matchday Boost instead).
 * Scoring: correct team 6pts (+4 if the game count is ALSO exact),
 * wrong team -2pts (+2 partial credit if the game count alone is exact).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Info, AlertTriangle, Check, Lock, X, Trophy, ChevronDown } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";
import { missedPlayoffsCopy } from "@/lib/playoffField";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";

function SeedChip({ seed }) {
    return (
        <span className="inline-flex w-6 h-6 items-center justify-center rounded-lg bg-black border border-white/10 font-mono text-[10px] font-bold text-zinc-400 shrink-0">
            {seed ?? "–"}
        </span>
    );
}

function seriesRecord(s) {
    let aWins = 0, bWins = 0;
    (s.games || []).forEach((g) => {
        if (!g.played) return;
        if (g.winner_team_id === s.team_a_id) aWins++;
        else if (g.winner_team_id === s.team_b_id) bWins++;
    });
    return { aWins, bWins };
}

export default function MlsSeriesPickModal({ open, onClose, tournamentId, participantId, teams, existingPicks, onPicksChanged, tournament }) {
    const [bracket, setBracket] = useState(null);
    const [picks, setPicks] = useState(existingPicks || {});
    const [savingAll, setSavingAll] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);

    const teamFor = (id) => (teams || []).find((t) => t.id === id);
    const nameFor = (id) => teamFor(id)?.name || id || "TBD";
    const seedFor = (id) => teamFor(id)?.seed;

    const existingPicksRef = useRef(existingPicks);
    existingPicksRef.current = existingPicks;

    useEffect(() => {
        if (!open || !tournamentId) return;
        const load = () => api.getMlsBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
        load();
        setPicks(existingPicksRef.current || {});
        const id = setInterval(load, 45000);
        return () => clearInterval(id);
    }, [open, tournamentId]);

    if (!open) return null;

    const r1 = (bracket?.rounds || []).find((r) => r.key === "round_one");
    const allSeries = (r1?.series || [])
        .filter((s) => s.status !== "tbd")
        .map((s) => ({ ...s, _validGames: r1.valid_games }));
    const notSetYet = !bracket || allSeries.length === 0;
    const openSeries = allSeries.filter((s) => s.status === "open");
    const liveSeries = allSeries.filter((s) => s.status === "locked");
    const completedSeries = allSeries.filter((s) => s.status === "final");
    const incompleteCount = openSeries.filter((s) => {
        const p = picks[s.id] || {};
        return !p.team_id || !p.game_count;
    }).length;

    // iter87.529 (user-requested) — same "seeding isn't final yet"
    // signal as MlsChampionPickModal/MlsDarkhorsePickModal: Round One's
    // 4v5/2v7/3v6 series are minted straight from CURRENT (still
    // moving) regular-season standings, well before the season ends
    // or the Wild Card games are even played, so they can show real,
    // pickable matchups that still shift later.
    const preSeasonLocked = !!bracket?.pre_season_locked;
    const fieldFinal = (bracket?.rounds || []).some((r) => (r.series || []).some((s) => (s.games || []).length > 0));
    const wcRound = (bracket?.rounds || []).find((r) => r.key === "wildcard");
    const pendingWildcardConfs = (wcRound?.series || [])
        .filter((s) => s.status === "open" || s.status === "locked")
        .map((s) => s.conference)
        .filter(Boolean);
    const pendingWildcard = pendingWildcardConfs.length > 0;

    const selectPick = (seriesId, teamId, gameCount) => {
        setPicks((prev) => ({ ...prev, [seriesId]: { team_id: teamId, game_count: gameCount ?? null } }));
    };

    const onSavePicks = async () => {
        const incomplete = openSeries.filter((s) => {
            const p = picks[s.id] || {};
            return !p.team_id || !p.game_count;
        });
        if (incomplete.length > 0) {
            toast.error(`Pick a team AND a game count for every series before saving — ${incomplete.length} series still incomplete`);
            return;
        }
        setSavingAll(true);
        try {
            await Promise.all(openSeries.map((s) => {
                const p = picks[s.id];
                return api.submitMlsSeriesPick(tournamentId, participantId, s.id, p.team_id, p.game_count);
            }));
            onPicksChanged?.(picks);
            toast.success("All picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Some picks failed to save — try again");
        } finally {
            setSavingAll(false);
        }
    };

    const onClearAll = async () => {
        const idsWithPicks = openSeries.filter((s) => picks[s.id]?.team_id || picks[s.id]?.game_count).map((s) => s.id);
        if (idsWithPicks.length === 0) {
            toast.info("No picks to clear yet");
            return;
        }
        setClearing(true);
        try {
            await api.clearMlsSeriesPicks(tournamentId, participantId, idsWithPicks);
            const next = { ...picks };
            idsWithPicks.forEach((id) => delete next[id]);
            setPicks(next);
            onPicksChanged?.(next);
            toast.success("Picks cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't clear picks");
        } finally {
            setClearing(false);
        }
    };

    const renderSeriesCard = (s) => {
        const pick = picks[s.id] || {};
        const locked = s.status !== "open";
        const isFinal = s.status === "final";
        const isLive = s.status === "locked";
        const record = (isFinal || isLive) ? seriesRecord(s) : null;
        const pickedWinner = isFinal && !!pick.team_id && pick.team_id === s.winner_team_id;
        const pickedLoser = isFinal && !!pick.team_id && pick.team_id !== s.winner_team_id;
        const outerBorderClass = pickedWinner ? "border-emerald-500" : pickedLoser ? "border-red-500" : "border-neutral-800";
        return (
            <div
                key={s.id}
                data-testid={`mls-series-${s.id}`}
                data-result={isFinal ? (pickedWinner ? "correct" : pickedLoser ? "incorrect" : "no-pick") : undefined}
                className={`brutal-card bg-neutral-900 border-2 ${outerBorderClass} p-4 space-y-3`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    {locked && <Lock className="w-3 h-3 text-zinc-500 shrink-0" data-testid={`mls-series-${s.id}-locked-icon`} />}
                    {s.conference && (
                        <span className="inline-flex items-center rounded-lg bg-neutral-950 border border-white/10 text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider text-zinc-400">
                            {s.conference}
                        </span>
                    )}
                    {record && (
                        <span data-testid={`mls-series-${s.id}-record`} className="ml-auto font-mono text-xs font-bold text-zinc-300 tabular-nums">
                            {record.aWins}–{record.bWins}
                        </span>
                    )}
                </div>
                {isFinal && (
                    <p data-testid={`mls-series-${s.id}-result`} className="text-[10px] uppercase tracking-widest text-zinc-400">
                        Final — <span className="text-cyber font-bold">{nameFor(s.winner_team_id)}</span> won in {s.deciding_game}
                    </p>
                )}
                {!isFinal && pick.team_id && pick.team_id !== s.team_a_id && pick.team_id !== s.team_b_id && (
                    <p data-testid={`mls-series-${s.id}-dropped-notice`} className="text-heat text-[10px] font-semibold flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        {missedPlayoffsCopy(nameFor(pick.team_id), tournament, { locked, subject: "your pick" })}
                    </p>
                )}
                <div>
                    {[[s.team_a_id, s.team_a_seed ?? seedFor(s.team_a_id)], [s.team_b_id, s.team_b_seed ?? seedFor(s.team_b_id)]].map(([tid, seed], idx) => {
                        const isSelected = pick.team_id === tid;
                        let rowClass = isSelected ? "border-volt bg-volt/20" : "border-transparent bg-neutral-800 hover:border-neutral-600";
                        if (isFinal && isSelected) {
                            rowClass = tid === s.winner_team_id ? "border-emerald-500 bg-emerald-500/10" : "border-red-500 bg-red-500/10";
                        }
                        return (
                            <button
                                key={tid || idx}
                                data-testid={`mls-series-${s.id}-pick-${idx === 0 ? "a" : "b"}`}
                                aria-pressed={isSelected}
                                disabled={locked || !tid}
                                onClick={() => selectPick(s.id, tid, pick.game_count)}
                                className={`flex items-center justify-between w-full p-3 mb-2 rounded-lg border-2 transition-all
                                    ${rowClass}
                                    ${locked && !isFinal ? "opacity-50 cursor-not-allowed" : locked ? "cursor-not-allowed" : ""}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <SeedChip seed={seed} />
                                    <TeamSwatch name={nameFor(tid)} sport="mls" size="w-6 h-6 text-[9px]" />
                                    <span className={`text-sm font-bold truncate text-left ${isSelected ? (isFinal ? (tid === s.winner_team_id ? "text-emerald-400" : "text-red-400") : "text-volt") : "text-white"}`}>{nameFor(tid)}</span>
                                </div>
                                {isSelected && <Check className={`w-4 h-4 shrink-0 ml-2 ${isFinal ? (tid === s.winner_team_id ? "text-emerald-400" : "text-red-400") : "text-volt"}`} />}
                            </button>
                        );
                    })}
                </div>
                <div>
                    <span className="text-xs text-neutral-500 font-mono mb-2 block uppercase tracking-widest">Games</span>
                    <div className="flex justify-between gap-2">
                        {(s._validGames || []).map((g) => {
                            const isPicked = pick.game_count === g;
                            let pillClass = isPicked
                                ? "bg-cyber text-black border-cyber font-bold"
                                : "border-neutral-700 bg-neutral-950 text-zinc-300 hover:border-neutral-500";
                            if (isFinal && isPicked) {
                                pillClass = s.deciding_game === g
                                    ? "bg-emerald-500/20 text-emerald-300 border-emerald-500 font-bold"
                                    : "bg-red-500/20 text-red-300 border-red-500 font-bold";
                            }
                            return (
                                <button
                                    key={g}
                                    data-testid={`mls-series-${s.id}-game-${g}`}
                                    aria-pressed={isPicked}
                                    disabled={locked || !pick.team_id}
                                    onClick={() => selectPick(s.id, pick.team_id, g)}
                                    className={`pill flex-1 py-2 text-center border-2 font-medium transition-colors
                                        ${pillClass}
                                        ${(locked || !pick.team_id) && !isFinal ? "opacity-40 cursor-not-allowed" : (locked || !pick.team_id) ? "cursor-not-allowed" : ""}`}
                                >
                                    in {g}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="mls-series-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="brutal-card max-w-lg w-full max-h-[90dvh] overflow-y-auto border-volt"
        >
            <div className="sticky top-0 z-10 bg-[#121212] border-b-2 border-neutral-800 px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                <h3 className="font-display font-extrabold uppercase text-lg flex items-center gap-2">
                    <Trophy className="w-[18px] h-[18px]" />
                    Series Predictor
                </h3>
                <button
                    onClick={onClose}
                    className="brutal-btn-ghost p-2 shrink-0"
                    data-testid="close-mls-series-pick-modal-btn"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-4 p-4 sm:p-6">
                <div data-testid="mls-series-how-it-works" className="text-neutral-400 text-[11px] pb-4 border-b-2 border-neutral-800 flex gap-2 items-start text-left">
                    <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                    <p className="leading-relaxed">
                        <span className="text-white font-bold uppercase tracking-tight">How it works:</span> pick a winner + exact game
                        count for <span className="text-white font-semibold">every</span> Round One series below (best-of-3).
                        Right team = <span className="text-cyber font-semibold">+6</span> (+4 more if the game
                        count is exact too). Wrong team = <span className="text-heat font-semibold">−2</span>
                        {" "}(+2 back if you still nailed the game count). Picks lock once that series' Game 1 starts.
                    </p>
                </div>

                {notSetYet && (
                    <p data-testid="mls-series-not-set-notice" className="text-xs text-zinc-500 -mt-1">
                        Round One matchups haven't been determined yet — check back once the Wild Card games wrap up. Sections below will fill in once they're set.
                    </p>
                )}

                {!preSeasonLocked && !fieldFinal && allSeries.length > 0 && (
                    <div
                        data-testid="mls-series-seeding-not-final-banner"
                        className="text-volt text-[11px] font-semibold flex items-start gap-1.5 bg-volt/10 border border-volt/40 rounded-lg px-3 py-2 -mt-1"
                    >
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                            Seeding isn't final yet — these matchups are projected only and can still shift before
                            the last day of the regular season. Come back to make your final pick once the season
                            ends and seeding is official.
                        </span>
                    </div>
                )}

                {!notSetYet && pendingWildcard && (
                    <p data-testid="mls-series-playin-pending-notice" className="text-xs text-zinc-500 -mt-1">
                        {pendingWildcardConfs.length > 1
                            ? `Both conferences' 1-seed Round One opponents (${pendingWildcardConfs.join(", ")}) are still waiting on their Wild Card games — they'll appear here once those are decided.`
                            : `The ${pendingWildcardConfs[0]} 1-seed's Round One opponent is still waiting on its conference's Wild Card game — it'll appear here once that's decided.`}
                    </p>
                )}
                <div className="space-y-4">
                    <div className="space-y-3">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold" data-testid="mls-series-open-heading">
                            Open — Make Your Pick ({openSeries.length})
                        </p>
                        {openSeries.length > 0 ? openSeries.map(renderSeriesCard) : (
                            <p data-testid="mls-series-no-open-notice" className="text-xs text-zinc-500">
                                No open series right now — check back once new matchups are set.
                            </p>
                        )}
                    </div>

                    <div className="space-y-3 pt-3 border-t-2 border-neutral-800">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5" data-testid="mls-series-live-heading">
                            <Lock className="w-3 h-3" /> Locked & Live ({liveSeries.length})
                        </p>
                        {liveSeries.length > 0 ? liveSeries.map(renderSeriesCard) : (
                            <p className="text-xs text-zinc-500" data-testid="mls-series-live-empty-notice">
                                Nothing locked or in progress right now.
                            </p>
                        )}
                    </div>

                    <Collapsible open={showCompleted} onOpenChange={setShowCompleted} className="pt-3 border-t-2 border-neutral-800">
                        <CollapsibleTrigger
                            data-testid="mls-series-completed-toggle"
                            className="flex items-center justify-between w-full text-left"
                        >
                            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold flex items-center gap-1.5">
                                <Trophy className="w-3 h-3" /> Completed ({completedSeries.length})
                            </span>
                            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showCompleted ? "rotate-180" : ""}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-3 pt-3" data-testid="mls-series-completed-content">
                            {completedSeries.length > 0 ? completedSeries.map(renderSeriesCard) : (
                                <p className="text-xs text-zinc-500" data-testid="mls-series-completed-empty-notice">
                                    No series have finished yet.
                                </p>
                            )}
                        </CollapsibleContent>
                    </Collapsible>
                </div>
            </div>
            <div className="sticky bottom-0 z-10 mt-4 flex items-center gap-2 bg-[#121212] border-t-2 border-neutral-800 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <button
                        type="button"
                        onClick={onClearAll}
                        disabled={clearing || savingAll}
                        data-testid="mls-series-clear-all-btn"
                        className="brutal-btn-ghost flex-1 py-2.5 text-xs font-bold uppercase tracking-widest disabled:opacity-40"
                    >
                        {clearing ? "Clearing…" : "Clear All Picks"}
                    </button>
                    <button
                        type="button"
                        onClick={onSavePicks}
                        disabled={savingAll || clearing || incompleteCount > 0}
                        data-testid="mls-series-save-picks-btn"
                        title={incompleteCount > 0 ? `Pick a team and a game count for every series first — ${incompleteCount} incomplete` : undefined}
                        className="brutal-btn flex-1 py-2.5 text-xs font-bold uppercase tracking-widest bg-volt text-black border-volt disabled:opacity-40"
                    >
                        {savingAll ? "Saving…" : "Save Picks"}
                    </button>
                </div>
        </BentoModal>
    );
}
